import { BarcodeType } from '~/modules/devices/types/barcode.type';
import { fetchOrdersV2 } from '~/modules/orders/services/ordersV2.service';
import { newRelic } from '~/utils/newRelic/newRelic';

import {
  type CreateScanBarcodeContentOptions,
  type ScanBarcodeContent,
} from '../types/scanContext.type';
import { normalizeObjectId } from './normalization.util';

export const createScanBarcodeContent = async ({
  barcodeValue,
  correlationId,
  fetchOrders = fetchOrdersV2,
  resolveBarcode,
}: CreateScanBarcodeContentOptions): Promise<ScanBarcodeContent | undefined> => {
  try {
    const barcode = await resolveBarcode(barcodeValue);
    if (barcode?.barcode_type !== BarcodeType.OrderShipment) return undefined;

    const orderObjectId = normalizeObjectId(barcode.barcode_content.order_id);
    const orderShipmentObjectId = normalizeObjectId(barcode.barcode_content.shipment_id);

    const { results } = await fetchOrders({ object_id: [orderObjectId] });
    const orderShipment = results
      .find(({ object_id }) => object_id === orderObjectId)
      ?.order_shipments.find(({ object_id }) => object_id === orderShipmentObjectId);

    if (!orderShipment) {
      newRelic.log('Failed to find Order Shipment', {
        customAttributes: {
          barcodeValue,
          correlationId,
          orderObjectId,
          orderShipmentObjectId,
        },
      });
      return undefined;
    }

    return {
      order_object_id: orderObjectId,
      order_shipment_object_id: orderShipmentObjectId,
      shipment_id: String(orderShipment.id),
      ...(orderShipment.transaction_object_id && {
        transaction_object_id: orderShipment.transaction_object_id,
      }),
    };
  } catch (error) {
    newRelic.noticeError('Failed to resolve scan barcode content', { correlationId, error });

    return undefined;
  }
};
